'use client'
import clsx from 'clsx'
import { PropsWithChildren, useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { Product, ProductsConnectionEdge } from '@/eshop/gql/graphql'
import Link from 'next/link'
import { makeProductDetailUrl } from '@/eshop/utils/url'
import { useQuery } from '@tanstack/react-query'
import {
    getProductsPricesAndAvailability,
    getUserProductsPricesAndAvailability,
} from '@/eshop/queries'
import { Option } from '@swan-io/boxed'
import { useK2Setup } from '@/eshop/hooks/useK2Setup'
import { useUser } from '@/eshop/hooks/useUser'
import {
    standardProductPriceVariant,
    useFormatPrice,
} from '@/eshop/utils/price'
import { LanguageDropdown } from '@/eshop/components/ProductList/LanguageDropdown'
import { getAvailabilityWrapper } from './functions'
import { priceAvailabilityQueue } from '@/eshop/utils/requestQueue'

const BRAND_LOGOS: Record<string, string | undefined> = {
    '275255864071432': 'acros.png',
    '276664613339329': 'fisher.png',
    '275255864071439': 'cpachem.png',
    '275255864071438': 'cpachem.png',
    '280500019137154': 'cpachem.png',
    '275414777856024': 'alfaaesar.png',
    '279795644501564': 'biosolve.png',
    '280500019137141': 'biosolve.png',
    '279795644501582': 'lachner.png',
    '280500019137162': 'lachner.png',
    Acros: 'acros.png',
    'CPA CHEm': 'cpachem.png',
    'CPA Chem': 'cpachem.png',
    'Alfa Aesar': 'alfaaesar.png',
    Fisher: 'fisher.png',
    Biosolve: 'biosolve.png',
    BIOSOLVE: 'biosolve.png',
    FisherBio: 'fisher.png',
    'Biosolve OEM': 'biosolve.png',
    CPACHEM: 'cpachem.png',
    'Lach-Ner': 'lachner.png',
    'Fisher Scientific': 'fisher.png',
}

export function getBrandLogo(brandRID: string, brandName: string) {
    const logo = BRAND_LOGOS[brandRID] ?? BRAND_LOGOS[brandName]
    if (logo) {
        return (
            // eslint-disable-next-line @next/next/no-img-element
            <img
                src={`/brands/${logo}`}
                alt={brandName}
                className="w-20 h-6 object-contain"
            />
        )
    }
    return <span>{brandName}</span>
}

interface Props {
    variants: ProductsConnectionEdge[]
    className?: string
    productGroupName: string
    brand?: string | null
}
export function Table({ variants, className, brand, productGroupName }: Props) {
    const { t } = useTranslation()
    const k2 = useK2Setup()
    const user = useUser()
    const formatPrice = useFormatPrice(k2.currencyCode)

    const anonymousPricesQueryKey = useMemo(
        () => [
            'product',
            'availability',
            'anonymous',
            k2.currency,
            k2.languageId,
            ...variants.map((v) => v.node.articleId),
        ],
        [variants, k2.currency, k2.languageId]
    )

    const userPricesQueryKey = useMemo(
        () => [
            'product',
            'availability',
            k2.currency,
            k2.languageId,
            ...variants.map((v) => v.node.articleId),
        ],
        [variants, k2.currency, k2.languageId]
    )

    const {
        data: anonymousData,
        isFetched: isAnonymousDataFetched,
        isFetching: isFetchingAnonymousData,
    } = useQuery({
        queryKey: anonymousPricesQueryKey,
        queryFn: ({ signal }) =>
            priceAvailabilityQueue.enqueue(
                (s) =>
                    getProductsPricesAndAvailability()(
                        {
                            quality: null,
                            sortingName: productGroupName,
                            languageId: k2.languageId,
                            currency: k2.currency,
                        },
                        s
                    ).toPromise(),
                signal
            ),
        enabled: user?.isAnonymous,
    })

    const {
        data: userData,
        isFetched: isUserDataFetched,
        isFetching: isFetchingUserData,
    } = useQuery({
        queryKey: userPricesQueryKey,
        queryFn: ({ signal }) =>
            priceAvailabilityQueue.enqueue(
                (s) =>
                    getUserProductsPricesAndAvailability()(
                        {
                            quality: null,
                            sortingName: productGroupName,
                            languageId: k2.languageId,
                            currency: k2.currency,
                        },
                        s
                    ).toPromise(),
                signal
            ),
        enabled: !user?.isAnonymous,
    })

    const getPriceAndAvailability = useCallback(
        (abbr: Product['abbr']) => {
            if (!user || user?.isAnonymous) {
                if (
                    !isAnonymousDataFetched ||
                    !anonymousData ||
                    !anonymousData.isOk ||
                    !anonymousData?.isOk()
                ) {
                    return Option.None()
                }
                return (
                    anonymousData?.match?.({
                        Ok: (d) =>
                            Option.fromNullable(
                                d?.pricesAndAvailability?.products
                                    ?.map(standardProductPriceVariant)
                                    ?.find((p) => p?.abbr === abbr)
                            ),
                        Error: () => Option.None(),
                    }) || Option.None()
                )
            } else {
                if (
                    !isUserDataFetched ||
                    !userData ||
                    !userData.isOk ||
                    !userData?.isOk()
                ) {
                    return Option.None()
                }
                return (
                    userData?.match?.({
                        Ok: (d) =>
                            Option.fromNullable(
                                d?.pricesAndAvailabilityUser?.products
                                    ?.map(standardProductPriceVariant)
                                    ?.find((p) => p?.abbr === abbr)
                            ),
                        Error: () => Option.None(),
                    }) || Option.None()
                )
            }
        },
        [
            anonymousData,
            isAnonymousDataFetched,
            userData,
            isUserDataFetched,
            user,
        ]
    )

    const hasData = useMemo(() => {
        if (!user || user.isAnonymous) {
            return !isFetchingAnonymousData && isAnonymousDataFetched
        }
        return !isFetchingUserData && isUserDataFetched
    }, [
        isAnonymousDataFetched,
        isFetchingAnonymousData,
        isFetchingUserData,
        isUserDataFetched,
        user,
    ])

    return (
        <div className={clsx('scrolling-touch')}>
            <table
                className={clsx('table min-w-max scrolling-touch', className)}
            >
                <thead>
                    <tr className="font-bold text-black">
                        <th className="uppercase">
                            {t('eshop.productlist.table.quality')}
                        </th>
                        <th className="uppercase">
                            <div className="flex flex-col">
                                <span>
                                    {t(
                                        'eshop.productlist.table.catalogueNumber'
                                    )}
                                </span>
                                <span className="text-xs text-gray-500">
                                    ({t('eshop.productlist.table.supplierCode')}
                                    )
                                </span>
                            </div>
                        </th>
                        <th className="uppercase">
                            {t('eshop.productlist.table.content')}
                        </th>
                        <th className="uppercase">
                            {t('eshop.productlist.table.manufacturer')}
                        </th>
                        <th className="uppercase text-right">
                            {t('eshop.productlist.table.price')}{' '}
                            <span className="text-xs font-normal">
                                ({t('eshop.price.baseSuffix')})
                            </span>
                        </th>
                        <th className="uppercase text-right">
                            {t('eshop.productlist.table.price')}{' '}
                            <span className="text-xs font-normal">
                                ({t('eshop.price.vatSuffix')})
                            </span>
                        </th>
                        <th></th>
                    </tr>
                </thead>
                <tbody className="text-base">
                    {variants.map((v, i, arr) => {
                        const variant = v.node
                        const info = variant.productLangItems.find(
                            (pl) => pl.languageId === k2.languageId
                        )
                        const externalSafetySheetLink =
                            variant.manufacturer?.translatedMetaDescriptionCalc

                        const priceAvailability = getPriceAndAvailability(
                            variant.abbr
                        )
                        const isSameQAsPrev =
                            arr[i - 1]?.node.productLangItems.find(
                                (pl) => pl.languageId === k2.languageId
                            )?.CFQuality === info?.CFQuality
                        return (
                            <tr key={variant.id} className="border-0">
                                <td
                                    className={clsx(
                                        isSameQAsPrev ? 'border-0' : 'border-t'
                                    )}
                                >
                                    {isSameQAsPrev ? '' : info?.CFQuality}
                                </td>
                                <td className="border-b">
                                    <div className="flex flex-col">
                                        <span>{variant.abbr}</span>
                                        {variant.abbr2 &&
                                            variant.abbr2 !== variant.abbr && (
                                                <span className="text-xs text-gray-500">
                                                    {variant.abbr2}
                                                </span>
                                            )}
                                    </div>
                                </td>
                                <td className="border-b">
                                    {info?.unit} {/* HERE */}
                                    <ConditionalCellBody hasData={hasData}>
                                        {priceAvailability.match({
                                            Some: (v) => {
                                                const availability =
                                                    getAvailabilityWrapper(
                                                        v.available ??
                                                            'Unavailable',
                                                        v.colorHexTxt ??
                                                            'black',
                                                        v.availableInStore ??
                                                            '',
                                                        v.colorHexTxtInStore ??
                                                            'black'
                                                    )

                                                return (
                                                    <div className="flex flex-col">
                                                        <span
                                                            className="text-xs"
                                                            style={{
                                                                color: availability
                                                                    .main
                                                                    ?.colorHexTxt,
                                                            }}
                                                        >
                                                            {
                                                                availability
                                                                    .main
                                                                    ?.available
                                                            }
                                                        </span>
                                                        {availability.external && (
                                                            <span
                                                                className="text-xs"
                                                                style={{
                                                                    color: availability
                                                                        .external
                                                                        .colorHexTxt,
                                                                }}
                                                            >
                                                                {
                                                                    availability
                                                                        .external
                                                                        .available
                                                                }
                                                            </span>
                                                        )}
                                                    </div>
                                                )
                                            },
                                            None: () => (
                                                <span className="text-blue-400 text-xs">
                                                    ---
                                                </span>
                                            ),
                                        })}
                                    </ConditionalCellBody>
                                </td>
                                <td className="border-b text-sm text-center">
                                    {info?.brandName &&
                                        getBrandLogo(
                                            v.node.brandRID!,
                                            info.brandName
                                        )}
                                </td>
                                <td className="border-b text-right">
                                    <ConditionalCellBody hasData={hasData}>
                                        {priceAvailability.match({
                                            Some: (v) =>
                                                formatPrice(v.priceNetto),
                                            None: () => '---',
                                        })}
                                    </ConditionalCellBody>
                                </td>
                                <td className="border-b text-right">
                                    <ConditionalCellBody hasData={hasData}>
                                        {priceAvailability.match({
                                            Some: (v) =>
                                                formatPrice(v.priceBrutto),
                                            None: () => '---',
                                        })}
                                    </ConditionalCellBody>
                                </td>
                                <td className="border-b">
                                    <div className="flex">
                                        <Link
                                            className="link link-hover text-blue-400 flex justify-center items-center tooltip"
                                            href={makeProductDetailUrl(
                                                variant.productGroup
                                                    ?.sortingNameLang || '',
                                                variant?.id
                                            )}
                                        >
                                            {t(
                                                'eshop.productlist.table.detail'
                                            )}
                                            <svg
                                                xmlns="http://www.w3.org/2000/svg"
                                                viewBox="0 0 20 20"
                                                fill="currentColor"
                                                aria-hidden="true"
                                                width="16"
                                                height="16"
                                            >
                                                <path
                                                    fillRule="evenodd"
                                                    d="M7.21 14.77a.75.75 0 01.02-1.06L11.168 10 7.23 6.29a.75.75 0 111.04-1.08l4.5 4.25a.75.75 0 010 1.08l-4.5 4.25a.75.75 0 01-1.06-.02z"
                                                    clipRule="evenodd"
                                                ></path>
                                            </svg>
                                        </Link>
                                        <div
                                            className={clsx(
                                                'link link-hover flex justify-center items-center tooltip dropdown',
                                                !!variant.specifications &&
                                                    variant.specifications
                                                        ?.length > 0
                                                    ? ' text-blue-400 cursor-pointer'
                                                    : 'text-gray-500 cursor-not-allowed'
                                            )}
                                            data-tip={t(
                                                'eshop.productlist.table.specifications'
                                            )}
                                        >
                                            <div tabIndex={0} role="button">
                                                <svg
                                                    xmlns="http://www.w3.org/2000/svg"
                                                    fill="none"
                                                    viewBox="0 0 24 24"
                                                    strokeWidth={1.5}
                                                    stroke="currentColor"
                                                    className="h-6"
                                                >
                                                    <path
                                                        strokeLinecap="round"
                                                        strokeLinejoin="round"
                                                        d="M19.5 14.25v-2.625a3.375 3.375 0 0 0-3.375-3.375h-1.5A1.125 1.125 0 0 1 13.5 7.125v-1.5a3.375 3.375 0 0 0-3.375-3.375H8.25m0 12.75h7.5m-7.5 3H12M10.5 2.25H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 0 0-9-9Z"
                                                    />
                                                </svg>
                                            </div>

                                            <LanguageDropdown
                                                data={
                                                    variant.specifications ?? []
                                                }
                                                tabIndex={0}
                                            />
                                        </div>
                                        <div
                                            className="link link-hover text-blue-400 flex justify-center items-center tooltip dropdown"
                                            data-tip={t(
                                                'eshop.productlist.table.safetySheets'
                                            )}
                                        >
                                            <div tabIndex={1} role="button">
                                                <svg
                                                    xmlns="http://www.w3.org/2000/svg"
                                                    fill="none"
                                                    viewBox="0 0 24 24"
                                                    strokeWidth={1.5}
                                                    stroke="currentColor"
                                                    className="h-6"
                                                >
                                                    <path
                                                        strokeLinecap="round"
                                                        strokeLinejoin="round"
                                                        d="M10.125 2.25h-4.5c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125v-9M10.125 2.25h.375a9 9 0 0 1 9 9v.375M10.125 2.25A3.375 3.375 0 0 1 13.5 5.625v1.5c0 .621.504 1.125 1.125 1.125h1.5a3.375 3.375 0 0 1 3.375 3.375M9 15l2.25 2.25L15 12"
                                                    />
                                                </svg>
                                            </div>
                                            <LanguageDropdown
                                                data={(
                                                    variant.safetyDataSheets ??
                                                    []
                                                ).concat(
                                                    externalSafetySheetLink
                                                        ? ([
                                                              {
                                                                  id: 'external',
                                                                  languageId: 1,
                                                                  shopRID: '',
                                                                  articleId: 0,
                                                                  url: externalSafetySheetLink.startsWith(
                                                                      'http'
                                                                  )
                                                                      ? externalSafetySheetLink
                                                                      : `https://${externalSafetySheetLink}`,
                                                                  translateKey:
                                                                      'eshop.productlist.table.externalSafetySheet',
                                                              },
                                                          ] as any)
                                                        : []
                                                )}
                                                tabIndex={1}
                                            />
                                        </div>
                                    </div>
                                </td>
                            </tr>
                        )
                    })}
                </tbody>
            </table>
        </div>
    )
}

function ConditionalCellBody({
    hasData,
    children,
}: PropsWithChildren<{ hasData: boolean }>) {
    return hasData ? (
        children
    ) : (
        <span className="loading loading-spinner loading-xs"></span>
    )
}
